import { Passenger } from '@models/micro/passenger.model';
import { PAX_TYPE } from '@app/enums';
import { Segment } from '@models/micro/segment.model';
import { Declared } from '@app/utils/pecTab';
import { FreeTextInfoList } from './response/viewDocuments.model';
import { getLegEndpoints } from '@app/utils/functions/leg-endpoints.utils';

const isNumberRegExp = new RegExp('^\\d{1,2}$');

export class BoardinPass {
  pectabMap: any;
  languageInfo: string = '';
  passengers: Passenger[];
  boardinPassInfo: any;
  constructor(private pectab: string, passengers: Passenger[], segments: any[], language: string) {
    this.languageInfo = language;
    this.pectabMap = TrasformPectab.separate(pectab);
    for (const passenger of passengers) {
      passenger.mapBoardingPass = passenger.travelDocument.map((bp) => TrasformPectab.separate(bp));
      this.buildSkyCleared(passenger);
      this.buildSkyPriority(passenger);
      this.buildDeclared(passenger);
      this.buildTwoConfort(passenger);
      this.buildCHD(passenger);
      this.buildSTB(passenger);
      this.buildTerminal(passenger, segments);
      this.buildLoyalty(passenger);
    }
    this.passengers = passengers;
  }
  getDoctos() {
    const bpBoarding = [];
    const indexPectab = this.pectab.indexOf('^01');
    for (const passenger of this.passengers) {
      const filteredIndices = this.filterDuplicateBoardingPassIndices(passenger.mapBoardingPass);
      for (const bpIndex of filteredIndices) {
        const bp = passenger.mapBoardingPass[bpIndex];
        const travelDoc = passenger.travelDocument[bpIndex];
        const indexTravelDoc = travelDoc.indexOf('^03');
        bpBoarding.push(
          `${travelDoc.substring(0, indexTravelDoc) + '^' + TrasformPectab.join(bp)}`
        );
      }
    }
    return {
      value: bpBoarding,
      pectab: `${
        this.pectab.substring(0, indexPectab) + '^' + TrasformPectab.join(this.pectabMap)
      }`,
    };
  }

  /**
   * Filtra boarding passes duplicados de un mismo pasajero.
   *
   * Dos documentos se consideran duplicados solo cuando pertenecen al **mismo
   * vuelo** (mismo carrier + número de vuelo) y al mismo asiento. Ese es el caso
   * del hidden stop: el pasajero permanece en la misma aeronave durante la escala
   * intermedia, por lo que le corresponde un único pase y se conserva el de
   * fecha/hora de salida más temprana (CI-16488).
   *
   * En una conexión los vuelos son distintos, así que cada segmento conserva su
   * propio pase aunque el número de asiento coincida entre ellos. Agrupar solo
   * por asiento descartaba pases legítimos cuando al pasajero le tocaba el mismo
   * número de asiento en las dos piernas (CI-22287).
   */
  private filterDuplicateBoardingPassIndices(mapBoardingPass: any[]): number[] {
    if (!mapBoardingPass || mapBoardingPass.length <= 1) {
      return mapBoardingPass ? mapBoardingPass.map((_, i) => i) : [];
    }

    // Agrupar índices por vuelo + asiento
    const groups: Record<string, number[]> = {};
    for (let i = 0; i < mapBoardingPass.length; i++) {
      const key = this.getBoardingPassGroupKey(mapBoardingPass[i]);
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(i);
    }

    const result: number[] = [];
    for (const indices of Object.values(groups)) {
      if (indices.length === 1) {
        result.push(indices[0]);
      } else {
        // Mismo vuelo y mismo asiento: conservar el de fecha/hora más temprana
        const earliestIndex = this.getEarliestBoardingPassIndex(mapBoardingPass, indices);
        result.push(earliestIndex);
      }
    }

    return result.sort((a, b) => a - b);
  }

  /**
   * Clave de agrupación de un boarding pass: carrier + número de vuelo + asiento.
   * Campos del pectab: '67' carrier, '64' número de vuelo, 'B2' asiento.
   */
  private getBoardingPassGroupKey(bp: any): string {
    const carrier = (bp?.[67] || '').trim();
    const flight = (bp?.[64] || '').trim();
    const seat = (bp?.['B2'] || '').trim();
    return `${carrier}|${flight}|${seat}`;
  }

  /**
   * Determina cuál boarding pass tiene la fecha/hora de salida más temprana.
   * Usa el campo '31' (fecha, ej: '26MAY') y 'C6' (hora, ej: '22:15') del mapBoardingPass.
   */
  private getEarliestBoardingPassIndex(mapBoardingPass: any[], indices: number[]): number {
    const months: Record<string, number> = {
      JAN: 0,
      FEB: 1,
      MAR: 2,
      APR: 3,
      MAY: 4,
      JUN: 5,
      JUL: 6,
      AUG: 7,
      SEP: 8,
      OCT: 9,
      NOV: 10,
      DEC: 11,
    };

    let earliestIndex = indices[0];
    let earliestTime = Infinity;

    for (const idx of indices) {
      const bp = mapBoardingPass[idx];
      const dateStr = (bp[31] || '').trim(); // ej: '26MAY'
      const timeStr = (bp['C6'] || '').trim(); // ej: '22:15'

      const day = parseInt(dateStr.substring(0, 2), 10) || 1;
      const monthStr = dateStr.substring(2).toUpperCase();
      const month = months[monthStr] ?? 0;

      const [hours, minutes] = timeStr.split(':').map(Number);

      // Crear timestamp comparable (año no importa, solo orden relativo)
      const timestamp = new Date(2000, month, day, hours || 0, minutes || 0).getTime();

      if (timestamp < earliestTime) {
        earliestTime = timestamp;
        earliestIndex = idx;
      }
    }

    return earliestIndex;
  }

  addAtFormat(position: any, properties?: string) {
    this.pectabMap[position] ??= properties;
  }
  addAtValue(el: object, position: any, value: any) {
    el[position] = value;
  }

  buildSkyCleared(passenger: Passenger) {
    if (passenger.paxType !== PAX_TYPE.INFANT && passenger.isSkyCleared()) {
      this.addAtFormat(49, '02K20');
      this.addAtFormat(51, '10K23');
      this.addAtFormat(52, '04K34F');
      for (const bp of passenger.mapBoardingPass) {
        this.addAtValue(bp, 49, '**');
        this.addAtValue(bp, 51, 'SKY CLEARED');
        this.addAtValue(bp, 52, '**');
      }
    }
    return this;
  }

  buildDeclared(passenger: Passenger) {
    if (
      passenger.paxType !== PAX_TYPE.INFANT &&
      !passenger.isSkyCleared() &&
      passenger.isIsDeclaredCOVIDTest()
    ) {
      this.addAtFormat(49, '02K20');
      this.addAtFormat(51, '10K23');
      this.addAtFormat(52, '04K34F');
      for (const bp of passenger.mapBoardingPass) {
        this.addAtValue(bp, 49, '**');
        this.addAtValue(bp, 51, Declared[this.languageInfo]);
        this.addAtValue(bp, 52, '**');
      }
    }
    return this;
  }

  buildTwoConfort(passenger: Passenger) {
    if (passenger.paxType !== PAX_TYPE.INFANT && passenger.hasDobleConfort()) {
      for (const bp of passenger.mapBoardingPass) {
        this.addAtValue(
          bp,
          'B2',
          `${this.getFreeTextBuildTwoConfort(passenger.freeTextInfoList) + 'AT'}`
        );
      }
    }
    return this;
  }

  getFreeTextBuildTwoConfort(freeTextInfoList: FreeTextInfoList[]): string {
    for (const free of freeTextInfoList) {
      if (free.editCode === 'NFST') {
        const posicionAT = free.text.indexOf('AT');
        const posicionInicio = posicionAT - 2;
        const valorAntesDeAT = free.text.slice(posicionInicio, posicionAT).trim();
        const palabras = free.text.split(' ');
        const segundaPalabra = palabras[1];
        return `${segundaPalabra}${valorAntesDeAT}`;
      }
    }
    return '';
  }

  buildCHD(passenger: Passenger) {
    if (passenger.paxType === PAX_TYPE.CHILD) {
      this.addAtFormat('B4', '05N46');
      for (const bp of passenger.mapBoardingPass) {
        this.addAtValue(bp, 'B4', 'CHILD');
      }
    }
    return this;
  }

  buildSTB(passenger: Passenger) {
    if (passenger.isStandBy) {
      for (const bp of passenger.mapBoardingPass) {
        if (bp['B3'] !== 'STB') {
          this.addAtValue(bp, 'B3', 'STB');
        }
      }
    }
    return this;
  }

  buildSkyPriority(passenger: Passenger) {
    const sky = 'SKY';
    let priority = 'PRIORITY';
    if (passenger.paxType !== PAX_TYPE.INFANT && passenger.frequentFlyerName === 'GOLD') {
      for (const bp of passenger.mapBoardingPass) {
        this.addAtValue(bp, 45, sky);
        this.addAtValue(bp, 47, sky);
        this.addAtValue(bp, 46, priority);
        this.addAtValue(bp, 48, priority);
      }
    }
    return this;
  }

  buildTerminal(passenger: Passenger, segments: Segment[]) {
    const legEndpoints = getLegEndpoints(segments);

    for (const [idx, bp] of passenger.mapBoardingPass.entries()) {
      const travelDoc = passenger.travelDocument[idx];
      const segment = this.resolveSegmentForDoc(travelDoc, segments, legEndpoints, idx);

      if (!segment) continue;

      if (segment.departureAirport === 'MEX') {
        this.addAtValue(bp, 60, bp[60] + ' T2');
      } else if (segment.arrivalAirport === 'MEX') {
        this.addAtValue(bp, 70, bp[70] + ' T2');
      }
    }
  }

  private resolveSegmentForDoc(
    travelDoc: string,
    segments: Segment[],
    legEndpoints: { origin: string; destination: string } | null,
    fallbackIdx: number
  ): Segment | undefined {
    // Match directo: el documento contiene origen y destino de un segmento individual
    const directMatch = segments.find(
      (s) => travelDoc?.includes(s.departureAirport) && travelDoc?.includes(s.arrivalAirport)
    );
    if (directMatch) return directMatch;

    // Hidden stop: el documento contiene los endpoints del leg completo (ej. GDL-JFK)
    // pero no coincide con ningún segmento individual (GDL-MEX, MEX-JFK)
    if (
      legEndpoints &&
      travelDoc?.includes(legEndpoints.origin) &&
      travelDoc?.includes(legEndpoints.destination)
    ) {
      return segments.find((s) => s.departureAirport === legEndpoints.origin) || segments[0];
    }

    return segments[fallbackIdx];
  }

  buildLoyalty(passenger: Passenger) {
    const { passengerInfo } = passenger;
    if (passengerInfo && passengerInfo?.ffProgram) {
      for (const bp of passenger.mapBoardingPass) {
        this.addAtFormat(84, '05O20L');
        this.addAtFormat(85, '02P20');
        this.addAtValue(bp, 84, 'Numero de viajero frecuente/Frequent flyer number');
        this.addAtValue(bp, 85, passengerInfo.ffNumber);
      }
    }

    return this;
  }
}

export class TrasformPectab {
  static separate(pectab: string): any {
    const data = {};
    const split = pectab.split('^');
    for (let [index, el] of split.entries()) {
      if (index > 6) {
        const extract = el.substring(0, 2);
        const key = Number(extract) || extract;
        if (key) {
          data[key] = el.substring(2);
        }
      }
    }
    return data;
  }

  static join(map: any): string {
    const entries = Object.entries(map)
      .sort((a: any, b: any) => {
        const keyA: any = a[0];
        const keyB: any = b[0];

        if (!isNaN(keyA) && !isNaN(keyB)) {
          return Number(keyA) - Number(keyB);
        } else {
          return keyA.localeCompare(keyB);
        }
      })
      .map(([key, value]) => `${isNumberRegExp.test(key) ? key.padStart(2, '0') : key}${value}`);

    return entries.join('^');
  }
}
